import React, { useState, useCallback, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Search, Replace, ChevronUp, ChevronDown, X } from 'lucide-react';
import { Textarea } from "@/components/ui/textarea";

interface SearchAndReplaceProps {
  content: string;
  onContentChange: (newContent: string) => void;
  isVisible: boolean;
  onClose: () => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  contentEditableRef?: React.RefObject<HTMLDivElement>;
  isWysiwygMode?: boolean;
}

const SearchAndReplace: React.FC<SearchAndReplaceProps> = ({
  content,
  onContentChange,
  isVisible,
  onClose,
  textareaRef,
  contentEditableRef,
  isWysiwygMode = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [matches, setMatches] = useState<Array<{ start: number; end: number; text: string }>>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(true);

  // Build a regex respecting current options
  const buildRegex = useCallback((searchText: string) => {
    const escaped = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = wholeWord ? `(?<![\\p{L}\\p{N}_])${escaped}(?![\\p{L}\\p{N}_])` : escaped;
    const flags = (caseSensitive ? 'g' : 'gi') + 'u';
    try {
      return new RegExp(pattern, flags);
    } catch {
      // Fallback for environments without Unicode property escapes / lookbehind
      const fallbackPattern = wholeWord ? `\\b${escaped}\\b` : escaped;
      return new RegExp(fallbackPattern, caseSensitive ? 'g' : 'gi');
    }
  }, [caseSensitive, wholeWord]);

  // Compute the current target text directly from the live DOM/content
  const getCurrentTarget = useCallback((): string => {
    if (isWysiwygMode && contentEditableRef?.current) {
      return contentEditableRef.current.textContent || '';
    }
    return content;
  }, [isWysiwygMode, contentEditableRef, content]);

  // Find all matches in the given text
  const computeMatches = useCallback((searchText: string, targetContent: string) => {
    if (!searchText.trim()) return [];
    const regex = buildRegex(searchText);
    const found: Array<{ start: number; end: number; text: string }> = [];
    let m;
    while ((m = regex.exec(targetContent)) !== null) {
      found.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
      if (m.index === regex.lastIndex) regex.lastIndex++;
    }
    return found;
  }, [buildRegex]);

  // Update matches when search term, options, or content changes
  useEffect(() => {
    const target = getCurrentTarget();
    const found = computeMatches(searchTerm, target);
    setMatches(found);
    setCurrentMatchIndex(found.length > 0 ? 0 : -1);
  }, [searchTerm, content, computeMatches, getCurrentTarget, wholeWord, caseSensitive]);

  // Navigate to next/previous match and highlight it
  const navigateToMatch = useCallback((index: number) => {
    // Re-scan against current live text to avoid stale offsets after re-renders
    const liveTarget = getCurrentTarget();
    const liveMatches = computeMatches(searchTerm, liveTarget);
    if (liveMatches.length === 0) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      return;
    }
    if (liveMatches.length !== matches.length) {
      setMatches(liveMatches);
    }
    const safeIndex = ((index % liveMatches.length) + liveMatches.length) % liveMatches.length;
    const match = liveMatches[safeIndex];
    setCurrentMatchIndex(safeIndex);

    // Highlight the match in the editor
    if (isWysiwygMode && contentEditableRef?.current) {
      const div = contentEditableRef.current;
      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();
      let current = 0;
      const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT, null);
      let node = walker.nextNode() as Text | null;
      let startSet = false;
      let endSet = false;

      while (node) {
        const len = node.nodeValue ? node.nodeValue.length : 0;
        if (!startSet && current + len >= match.start) {
          range.setStart(node, Math.max(0, match.start - current));
          startSet = true;
        }
        if (!endSet && current + len >= match.end) {
          range.setEnd(node, Math.max(0, match.end - current));
          endSet = true;
          break;
        }
        current += len;
        node = walker.nextNode() as Text | null;
      }

      if (startSet && endSet) {
        selection.removeAllRanges();
        selection.addRange(range);
        div.focus();
        const rect = range.getBoundingClientRect();
        const outOfView = rect.top < 80 || rect.bottom > window.innerHeight - 80;
        if (outOfView) {
          const startNode = range.startContainer;
          const el = (startNode.nodeType === Node.ELEMENT_NODE
            ? startNode
            : startNode.parentElement) as HTMLElement | null;
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    } else if (textareaRef?.current) {
      const textarea = textareaRef.current;
      textarea.focus();
      textarea.setSelectionRange(match.start, match.end);
      textarea.scrollTop = textarea.scrollHeight * (match.start / Math.max(1, liveTarget.length)) - textarea.clientHeight / 2;
    }
  }, [matches.length, isWysiwygMode, contentEditableRef, textareaRef, searchTerm, getCurrentTarget, computeMatches]);

  const goToNext = () => {
    if (matches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % matches.length;
    navigateToMatch(nextIndex);
  };

  const goToPrevious = () => {
    if (matches.length === 0) return;
    const prevIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : matches.length - 1;
    navigateToMatch(prevIndex);
  };

  const replaceAll = () => {
    if (!searchTerm.trim() || matches.length === 0) return;
    const regex = buildRegex(searchTerm);
    const newContent = content.replace(regex, replaceTerm);
    onContentChange(newContent);
    setSearchTerm('');
    setReplaceTerm('');
  };

  const replaceCurrentMatch = () => {
    if (matches.length === 0 || currentMatchIndex < 0) return;

    const match = matches[currentMatchIndex];
    const newContent = content.slice(0, match.start) + replaceTerm + content.slice(match.end);
    onContentChange(newContent);
  };

  if (!isVisible) return null;

  return (
    <Card className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 w-96 p-4 shadow-lg bg-background border">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4" />
          <span className="font-medium text-sm">Search & Replace</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-6 w-6 p-0"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3">
        {/* Search Input */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (matches.length === 0) return;
                  if (e.shiftKey) {
                    goToPrevious();
                  } else {
                    // First Enter: select current match. Subsequent: advance.
                    const sel = window.getSelection();
                    const hasActiveSelection = sel && sel.toString().length > 0;
                    if (hasActiveSelection) goToNext();
                    else navigateToMatch(currentMatchIndex >= 0 ? currentMatchIndex : 0);
                  }
                }
              }}
              className="flex-1"
              autoFocus
            />
            <div className="flex items-center gap-1">
              {matches.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {currentMatchIndex + 1} of {matches.length}
                </Badge>
              )}
              {matches.length === 0 && searchTerm && (
                <Badge variant="destructive" className="text-xs">
                  No matches
                </Badge>
              )}
            </div>
          </div>

          {/* Navigation buttons */}
          {matches.length > 0 && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevious}
                className="h-7 w-7 p-0"
              >
                <ChevronUp className="w-3 h-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNext}
                className="h-7 w-7 p-0"
              >
                <ChevronDown className="w-3 h-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCaseSensitive(!caseSensitive)}
                className={`h-7 px-2 text-xs ${caseSensitive ? 'bg-primary text-primary-foreground' : ''}`}
                title="Match case"
              >
                Aa
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWholeWord(!wholeWord)}
                className={`h-7 px-2 text-xs ${wholeWord ? 'bg-primary text-primary-foreground' : ''}`}
                title="Whole word"
              >
                W
              </Button>
            </div>
          )}
        </div>

        {/* Replace Input */}
        <div className="space-y-2">
          <Input
            placeholder="Replace with..."
            value={replaceTerm}
            onChange={(e) => setReplaceTerm(e.target.value)}
          />
          
          {matches.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={replaceCurrentMatch}
                className="flex-1"
              >
                Replace
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={replaceAll}
                className="flex-1"
              >
                <Replace className="w-3 h-3 mr-1" />
                Replace All
              </Button>
            </div>
          )}
        </div>

        {/* Match counter */}
        {searchTerm && (
          <div className="text-xs text-muted-foreground">
            {matches.length > 0 
              ? `Found ${matches.length} match${matches.length !== 1 ? 'es' : ''}`
              : 'No matches found'
            }
          </div>
        )}
      </div>
    </Card>
  );
};

export default SearchAndReplace;
